<template>
  <div class="air-table" :class="{ safari: $isSafari(), 'not-safari': !$isSafari() }">
    <vxe-table
      class="air-table-main"
      ref="table"
      :data="data"
      size="mini"
      height="auto"
      :border="border"
      show-overflow="tooltip"
      :show-header-overflow="showHeaderOverflow"
      :header-row-class-name="`air-table-header ${headerRowClass || ''}`"
      header-cell-class-name="air-table-header-cell"
      row-class-name="air-table-row"
      cell-class-name="air-table-cell"
      :cell-style="cellStyle"
      :column-config="{ resizable: true, isHover: columnHighlight, isCurrent: columnHighlight }"
      :row-config="{ isHover: rowHighlight, isCurrent: rowHighlight }"
      :filter-config="{ remote: remote }"
      :sort-config="{ multiple: true, chronological: true }"
      :tooltip-config="{showAll: true, contentMethod: showTooltipMethod}"
      resizable
      highlight-current-row
      highlight-hover-row
      highlight-current-column
      highlight-hover-column
      @filter-change="filterChange"
      @sort-change="sortChange"
      @cell-click="cellClick"
      @header-cell-click="headerCellClick"
      v-bind="$attrs"
      v-on="$listeners"
      :scroll-y="{enabled:false}"
    >
      <vxe-table-column v-if="showCheckbox" type="checkbox" width="50" fixed="left" align="center"></vxe-table-column>
      <vxe-table-column v-if="showSeq" type="seq" :title="$t('seq')" width="50" fixed="left" align="center"></vxe-table-column>
      <template v-for="(column, columnIndex) in columns">
        <!-- 常规列 -->
        <vxe-table-column
          :key="column.field + columnIndex"
          v-if="showColumn(column) && !column.isGroup"
          v-bind="column"
          :class-name="columnClass(column)"
          :header-class-name="columnHeaderClass(column)"
          :filters="column.filterType ? [{data: ''}] : null"
          :filter-render="handleFilterRender(column)"
          :tree-node="column.treeNode"
          :params="column"
          :title="column[getTitle] ? column[getTitle] : column.title"
          :min-width="column.minWidth ? column.minWidth : 140"
        >
          <!-- 表头自定义 -->
          <template v-if="column.headerCustom" #header="scope">
            <span class="font-14">{{ scope[column[getTitle] ? column[getTitle] : column.title] }}</span>
            <span v-if="showOperation||column.property === 'a0300722'" @click="headerIconClick(scope.column, column)">
              <el-icon v-if="column.headerCustom === 'add'" class="el-icon-circle-plus-outline table-header-icon"></el-icon>
              <el-icon v-if="column.headerCustom === 'edit'" class="el-icon-edit-outline table-header-icon"></el-icon>
              </span>
          </template>
          <!-- 自定义模板 -->
          <template v-if="column.custom" #default="scope">
            <slot :name="column.field" v-bind="scope"></slot>
          </template>
          <!-- 格式化函数 -->
          <template v-else-if="column.formatFunction" #default="{ row }">
            <span style="width: 100%;">{{ column.formatFunction(row, column) }}</span>
          </template>
          <!-- 格式化文本 -->
          <template v-else-if="column.formatText" #default="{ row }">
            <span style="width: 100%;">{{ $formatText(column.formatText, column.formatKeys, row) }}</span>
          </template>
          <!-- 金额 -->
          <template v-else-if="column.money" #default="{ row }">
            <span style="width: 100%;">{{ $showMoney(row[column.field]) }}</span>
          </template>
        </vxe-table-column>
        <!-- 多级表头 -->
        <vxe-table-column v-if="column.isGroup" :title="column[getTitle] ? column[getTitle] : column.title" :align="column.align" :min-width="column.minWidth ? column.minWidth : 140">
          <!-- 表头自定义 -->
          <template v-if="column.headerCustom" #header="scope">
            <span v-if="showOperation||column.property === 'a0300722'" @click="headerIconClick(scope.column, column)">
              <el-icon v-if="column.headerCustom === 'add'" class="el-icon-circle-plus-outline table-header-icon"></el-icon>
              <el-icon v-if="column.headerCustom === 'edit'" class="el-icon-edit-outline table-header-icon"></el-icon>
            </span>
            <span class="font-14">{{ scope[column[getTitle] ? column[getTitle] : column.title] }}</span>
          </template>
          <template v-for="(child, childIndex) in column.children">
            <vxe-table-column
              :min-width="column.minWidth ? column.minWidth : 140"
              :key="child.field + childIndex"
              v-if="showColumn(child) && !child.isGroup"
              v-bind="child"
              :class-name="columnClass(child)"
              :header-class-name="columnHeaderClass(child)"
              :filters="child.filterType ? [{data: ''}] : null"
              :filter-render="handleFilterRender(child)"
            >
              <!-- 表头自定义 -->
              <template v-if="child.headerCustom" #header="{ column }">
                <span v-if="showOperation||column.property === 'a0300722'" @click="headerIconClick(column, child)">
                  <el-icon v-if="child.headerCustom === 'add'" class="el-icon-circle-plus-outline table-header-icon"></el-icon>
                  <el-icon v-if="child.headerCustom === 'edit'" class="el-icon-edit-outline table-header-icon"></el-icon>
                </span>
                <span class="font-14">{{ column[getTitle] ? column[getTitle] : column.title }}</span>
              </template>
              <!-- 自定义模板 -->
              <template v-if="child.custom" #default="scope">
                <slot :name="child.field" v-bind="scope"></slot>
              </template>
              <!-- 格式化函数 -->
              <template v-else-if="child.formatFunction" #default="{ row }">
                <span style="width: 100%;">{{ child.formatFunction(row, child) }}</span>
              </template>
              <!-- 格式化文本 -->
              <template v-else-if="child.formatText" #default="{ row }">
                <span style="width: 100%;">{{ $formatText(child.formatText, child.formatKeys, row) }}</span>
              </template>
              <!-- 金额 -->
              <template v-else-if="child.money" #default="{ row }">
                <span style="width: 100%;">{{ $showMoney(row[child.field]) }}</span>
              </template>
            </vxe-table-column>

            <!-- 三级表头 -->
            <vxe-table-column v-if="child.isGroup" :title="child.title" :align="child.align" :min-width="column.minWidth ? column.minWidth : 140">
              <!-- 表头自定义 -->
              <template v-if="child.headerCustom" #header="{ column }">
                <span v-if="showOperation||column.property === 'a0300722'" @click="headerIconClick(column, child)">
                  <el-icon v-if="child.headerCustom === 'add'" class="el-icon-circle-plus-outline table-header-icon"></el-icon>
                  <el-icon v-if="child.headerCustom === 'edit'" class="el-icon-edit-outline table-header-icon"></el-icon>
                </span>
                <span class="font-14">{{ column[getTitle] ? column[getTitle] : column.title }}</span>
              </template>
              <template v-if="showColumn(third)">
                <vxe-table-column
                  :min-width="column.minWidth ? column.minWidth : 140"
                  v-for="(third, thirdIndex) in child.children"
                  :key="third.field + thirdIndex"
                  v-bind="third"
                  :class-name="columnClass(third)"
                  :header-class-name="columnHeaderClass(third)"
                  :filters="third.filterType ? [{data: ''}] : null"
                  :filter-render="handleFilterRender(third)"
                >
                  <!-- 表头自定义 -->
                  <template v-if="third.headerCustom" #header="{ column }">
                    <span v-if="showOperation||column.property === 'a0300722'" @click="headerIconClick(column, third)">
                      <el-icon v-if="third.headerCustom === 'add'" class="el-icon-circle-plus-outline table-header-icon"></el-icon>
                      <el-icon v-if="third.headerCustom === 'edit'" class="el-icon-edit-outline table-header-icon"></el-icon>
                    </span>
                    <span class="font-14">{{ column[getTitle] ? column[getTitle] : column.title }}</span>
                  </template>
                  <!-- 自定义模板 -->
                  <template v-if="third.custom" #default="scope">
                    <slot :name="third.field" v-bind="scope"></slot>
                  </template>
                  <!-- 格式化函数 -->
                  <template v-else-if="third.formatFunction" #default="{ row }">
                    <span style="width: 100%;">{{ third.formatFunction(row, third) }}</span>
                  </template>
                  <!-- 格式化文本 -->
                  <template v-else-if="third.formatText" #default="{ row }">
                    <span style="width: 100%;">{{ $formatText(third.formatText, third.formatKeys, row) }}</span>
                  </template>
                  <!-- 金额 -->
                  <template v-else-if="third.money" #default="{ row }">
                    <span style="width: 100%;">{{ $showMoney(row[third.field]) }}</span>
                  </template>
                </vxe-table-column>
              </template>
            </vxe-table-column>
          </template>
        </vxe-table-column>
      </template>
      <!-- 操作列 -->
      <vxe-table-column
        v-if="showOperation"
        :title="getLang == 'en' ? 'Operation' : '操作'"
        align="center"
        :min-width="operationColumnWidth"
        fixed="right"
      >
        <template #default="scope">
          <slot name="operation" v-bind="scope"></slot>
        </template>
      </vxe-table-column>
      <!-- 空数据 -->
      <template v-if="showEmpty" #empty>
        <div class="flex vertical center">
          <img v-if="smallEmpty" src="@/air/assets/img/common/empty.png" style="width: 100px; height: 100px;" />
          <img v-else src="@/air/assets/img/common/empty.png" style="width: 147px; height: 146px;" />
          <span class="font-14" style="color: #c9c9c9; margin-top: 20px">{{ $t('noData') }}</span>
        </div>
      </template>
    </vxe-table>

    <!-- 仅用于导出的隐藏表格 -->
    <vxe-table
      v-if="exportColumns.length"
      style="display: none;"
      ref="exportTable"
      :data="exportData"
      :header-row-class-name="`air-table-header ${headerRowClass || ''}`"
      header-cell-class-name="air-table-header-cell"
      row-class-name="air-table-row"
      cell-class-name="air-table-cell"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <vxe-table-column v-if="showExportSeq" type="seq" :title="$t('seq')" width="50" fixed="left"></vxe-table-column>
      <template v-for="(column, columnIndex) in exportColumns">
        <!-- 常规列 -->
        <vxe-table-column
          :key="column.title + columnIndex"
          v-if="!column.isGroup"
          v-bind="column"
          :class-name="columnClass(column)"
          :header-class-name="columnHeaderClass(column)"
        >
          <!-- 自定义模板 -->
          <template v-if="column.custom" #default="scope">
            <slot :name="column.field" v-bind="scope"></slot>
          </template>
          <!-- 格式化函数 -->
          <template v-else-if="column.formatFunction" #default="{ row }">
            <span style="width: 100%;">{{ column.formatFunction(row, column) }}</span>
          </template>
          <!-- 格式化文本 -->
          <template v-else-if="column.formatText" #default="{ row }">
            <span style="width: 100%;">{{ $formatText(column.formatText, column.formatKeys, row) }}</span>
          </template>
          <!-- 金额 -->
          <template v-else-if="column.money" #default="{ row, cell }">
            <span style="width: 100%;">{{ $showMoney(cell) }}</span>
          </template>
        </vxe-table-column>
        <!-- 多级表头 -->
        <vxe-table-column v-else :title="column[getTitle] ? column[getTitle] : column.title" :align="column.align">
          <vxe-table-column
            v-for="(child, childIndex) in column.children"
            :key="child.field + childIndex"
            v-bind="child"
            :class-name="columnClass(child)"
            :header-class-name="columnHeaderClass(child)"
          >
            <!-- 自定义模板 -->
            <template v-if="child.custom" #default="scope">
              <slot :name="child.field" v-bind="scope"></slot>
            </template>
            <!-- 格式化函数 -->
            <template v-else-if="child.formatFunction" #default="{ row }">
              <span style="width: 100%;">{{ child.formatFunction(row, child) }}</span>
            </template>
            <!-- 格式化文本 -->
            <template v-else-if="child.formatText" #default="{ row }">
              <span style="width: 100%;">{{ $formatText(child.formatText, child.formatKeys, row) }}</span>
            </template>
            <!-- 金额 -->
            <template v-else-if="child.money" #default="{ row }">
              <span style="width: 100%;">{{ $showMoney(row[child.field]) }}</span>
            </template>
          </vxe-table-column>
        </vxe-table-column>
      </template>
    </vxe-table>
  </div>
</template>

<script>
  import { validatenull } from "@/util/validate";

  export default {
  name: 'airTable',
  props: {
    remote: {
      type: Boolean,
      default: true
    },
    columns: {
      type: Array,
      default () {
        return []
      }
    },
    exportColumns: {
      type: Array,
      default () {
        return []
      }
    },
    data: {
      type: Array,
      default () {
        return []
      }
    },
    border: {
      type: String,
      default: 'inner'
    },
    showCheckbox: {
      type: Boolean,
      default: true
    },
    showSeq: {
      type: Boolean,
      default: true
    },
    showOperation: {
      type: Boolean,
      default: true
    },
    headerRowClass: {
      type: String,
      default: ''
    },
    operationColumnWidth: {
      type: Number,
      default: 120
    },
    showExportSeq: {
      type: Boolean,
      default: true
    },
    showEmpty: {
      type: Boolean,
      default: true
    },
    smallEmpty: {
      type: Boolean,
      default: false
    },
    columnHighlight: {
      type: Boolean,
      default: true
    },
    rowHighlight: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      exportData: [],
      dateFormatMap: {
        year: 'yyyy',
        month: 'yyyy-MM',
        date: 'yyyy-MM-dd',
        daterange: 'yyyy-MM-dd',
        monthrange: 'yyyy-MM'
      }
    }
  },
  watch: {
    data: {
      handler(val) {
        this.exportData = val
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    setCheckbox(row, checked) {
      this.$refs.table.setCheckboxRow(row, checked)
    },
    setCheckboxAll(checked) {
      this.$refs.table.setAllCheckboxRow(checked)
    },
    clearCheckbox() {
      this.$refs.table.clearCheckboxRow()
    },
    showColumn(column) {
      return column.showFunction ? column.showFunction(this.data, column) : true
    },
    columnClass(column) {
      return column.className || ''
    },
    columnHeaderClass(column) {
      let className = column.headerClassName ? `${column.headerClassName} ` : ''
      if (column.sortable && !column.filterType) {
        className += 'only-sort'
      }
      if (!column.sortable && column.filterType) {
        className += 'only-filter'
      }
      return className
    },
    cellStyle ({ row, rowIndex, column, columnIndex }) {
      if ((column.property === 'a0300712' && row.a0300712HasEdit === 'yellow')
        || (column.property === 'a0300761' && row.a0300761HasEdit === 'yellow')
        || (column.property === 'a0300722' && row.a0300722HasEdit === 'yellow')
        || (column.property === 'a0300729' && row.a0300729HasEdit === 'yellow')
        || (column.property === 'a0300733' && row.a0300733HasEdit === 'yellow')
        || (column.property === 'a0300735' && row.a0300735HasEdit === 'yellow')
        || (column.property === 'a0300737' && row.a0300737HasEdit === 'yellow')
        || (column.property === 'a0300765' && row.a0300765HasEdit === 'yellow')
        || (column.property === 'a0300766' && row.a0300766HasEdit === 'yellow')
        || (column.property === 'a0300768' && row.a0300768HasEdit === 'yellow')
        || (column.property === 'a0300769' && row.a0300769HasEdit === 'yellow')
        || (column.property === 'a0300771' && row.a0300771HasEdit === 'yellow')
        || (column.property === 'a0300772' && row.a0300772HasEdit === 'yellow')
        || (column.property === 'a0300773' && row.a0300773HasEdit === 'yellow')
        || (column.property === 'a0300774' && row.a0300774HasEdit === 'yellow')
        || (column.property === 'a0300775' && row.a0300775HasEdit === 'yellow')
        || (column.property === 'a0300776' && row.a0300776HasEdit === 'yellow')
        || (column.property === 'a0300751' && row.a0300751HasEdit === 'yellow')
        || (column.property === 'a0300780' && row.a0300780HasEdit === 'yellow')
        || (column.property === 'a0300781' && row.a0300781HasEdit === 'yellow')
        ) {
        return {
          backgroundColor: '#ffff99'
        }
      }
      let HiddenRating = column.property+'HiddenRating';
      if((row[HiddenRating] === '10020001')){
        let propertyName=column.property
        row[propertyName]='**'
        return {
          backgroundColor: '#ffff99'
        }
      }
    },
    showTooltipMethod ({ type, column, row, items }) {
      if (type === 'body' && column.title === '阶段有效') {
        return `${row.a0300717} ~ ${row.a0300718}`
      }
    },
    handleFilterRender(column) {
      if (column.filterType === 'input') {
        return {
          name: 'ElInput',
          props: {placeholder: this.$t('inputPlaceholder')},
          events: {keyup: this.enterFilterEvent}
        }
      }
      if (column.filterType === 'select') {
        let filterOptions = [];
        if (column.filterOptions && column.filterOptions.length) {
          filterOptions = column.filterOptions.map(item => {
            return {
              ...item,
              codeItemEnName: validatenull(item.codeItemEnName) ? item.codeItemName : item.codeItemEnName
            }
          })
        }
        return {
          name: 'ElSelect',
          optionProps: column.filterOptionProps || { value: 'codeItemId', label: this.getDictLabel },
          options: filterOptions || [],
          props: {multiple: column.filterMultiple, filterable: true, collapseTags: true}
        }
      }
      if (this.dateFormatMap[column.filterType]) {
        return {
          name: 'ElDatePicker',
          props: {
            type: column.filterDatePickerType || column.filterType,
            format: column.filterDateFormat || this.dateFormatMap[column.filterType],
            valueFormat: column.filterDateValueFormat || this.dateFormatMap[column.filterType],
            rangeSeparator: this.$t('rangeSeparator'),
            startPlaceholder: this.$t('startDate'),
            endPlaceholder: this.$t('endDate'),
            placeholder: this.$t('pleaseSelect')
          }
        }
      }
    },
    // 回车提交
    enterFilterEvent({context, column}, event) {
      if (event.keyCode === 13) {
        context.confirmFilter()
      }
    },
    filterChange(e) {
      this.exportData = e.$table.afterFullData
      // 组装数据
      let filterData = {}
      for (let filter of e.filters) {
        let field = filter.field
        if (field.endsWith('Text')) {
          // 如果以Text结尾，则是字典转义项，作为查询条件时应把结尾的Text去掉
          field = field.replace(/(.*)Text/,'$1')
        }
        filterData[field] = filter.datas.length <= 1 ? filter.datas[0] : filter.datas
      }
      this.$emit('change-filter', {
        filterData,
        originData: e
      })
    },
    sortChange(e) {
      this.exportData = e.$table.afterFullData
      this.$emit('sort-change', e)
    },
    cellClick(e) {
      this.$emit('cell-click', e)
    },
    headerCellClick(e) {
      this.$emit('header-cell-click', e)
    },
    exportExcel(filename, exportData, message) {
      const tableRef = this.exportColumns.length ? 'exportTable' : 'table'
      const checkedData = this.$refs.table.getCheckboxRecords();
      const columnTitle = this.getLang == 'en' ? 'Operation' : '操作';
      exportData = exportData || (checkedData.length ? checkedData : this.exportData)
      this.$refs[tableRef].exportData({
        data: exportData,
        filename,
        sheetName: 'Sheet1',
        type: 'xlsx',
        message,
        columnFilterMethod({ column, $columnIndex }) {
          return column.type !== 'checkbox' && column.title !== columnTitle;
        }
      })
    },
    $getOS() {
      const agent = navigator.userAgent.toLowerCase()
      const isMac = /macintosh|mac os x/i.test(navigator.userAgent)
      console.log('浏览器及系统：', agent)
      if (agent.indexOf("win") >= 0 || agent.indexOf("wow") >= 0) {
        return 'win'
      }
      if (isMac) {
        return 'mac'
      }
      return 'unknown'
    },
    $isSafari() {
      return /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
    },
    /**
     * 格式化文本
     * @param text 需要格式化的文本，占位符格式为{0}，注意要从0开始，按顺序匹配format key
     * @param formatKeys 要作为插值的key数组，或者直接要插入的值数组
     * @param data 作为插值的对象，对象内匹配到的key，会将value替换进去
     */
    $formatText(text, formatKeys, data) {
      if (formatKeys && formatKeys.length) {
        formatKeys.forEach((formatKey, index) => {
          text = text.replace(`{${index}}`, data ? data[formatKey] : formatKey)
        })
      }
      return text
    },
    headerIconClick(columnConfig, column) {
      this.$emit('header-icon-click', {
        ...columnConfig,
        ...column
      })
    }
  },
  computed: {
    showHeaderOverflow() {
      if (this.$userInfo.cusId === '1019672113180573696') {
        return false;
      } else {
        return 'tooltip'
      }
    },
    getLang() {
      return localStorage.getItem('lang');
    },
    getTitle() {
      return this.getLang == 'en' ? 'itemEnName' : 'title';
    },
    getGroupTitle() {
      return this.getLang == 'en' ? 'itemSetName' : 'itemSetNameEn';
    },
    getDictLabel() {
      return this.getLang == 'en' ? 'codeItemEnName' : 'codeItemName';
    },
    getColumnByField(field) {
      return this.$refs.table.getColumnByField(field);
    },
    hideColumn(column) {
      return this.$refs.table.hideColumn(column);
    },
    resetColumn() {
      return this.$refs.table.resetColumn();
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/air/assets/css/index.scss";
@import "~@/air/assets/css/common.scss";
@import "~@/air/assets/css/biz.scss";

.table-header-icon {
  margin-right: 10px;
  cursor: pointer;
  vertical-align: middle;
  font-size: 18px;
  color: #409eff;
}
</style>
